"use client";

// Libraries
import Head from "next/head";
import { useForm } from "react-hook-form";
import { Link, useRouter } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { getProviders, signIn, useSession } from "next-auth/react";
import Logo from "@/components/ui/Logo/Logo";

// Components
import Button from "@/components/ui/Button/Button";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Card, CardContent } from "@/components/ui/card";
import BelieversPanel from "@/components/ui/BelieversPanel/BelieversPanel";

// Mémorise la dernière méthode de connexion utilisée sur cet appareil afin
// d'afficher le badge "Dernière connexion" au prochain passage sur /login.
const LAST_LOGIN_KEY = "blvmy_last_login";

const rememberLoginMethod = (method) => {
    try {
        localStorage.setItem(LAST_LOGIN_KEY, method);
    } catch (e) {
        // localStorage indisponible (navigation privée, etc.) : on ignore
    }
};

export default function Login({ session, ...props }) {
    // Variables
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const router = useRouter();
    const searchParams = useSearchParams();
    const firstName = searchParams.get("firstName");
    const queryError = searchParams.get("error");
    const redirect = searchParams.get("redirect");
    const t = useTranslations();

    if (session) {
        router.push("/");
    }

    // States
    const [isLoading, setIsLoading] = useState(false);
    // Double authentification (étape 2)
    const [step, setStep] = useState(1);
    const [pendingAuth, setPendingAuth] = useState(null);
    const [code, setCode] = useState("");
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [rememberDevice, setRememberDevice] = useState(true);
    // Dernière méthode mémorisée sur cet appareil ("google" | "apple" | "credentials")
    const [lastMethod, setLastMethod] = useState(null);
    // Le bouton Apple ne s'affiche que si le provider est configuré côté
    // serveur (les 4 variables d'env APPLE_* présentes) : on interroge
    // NextAuth via getProviders au montage.
    const [appleAvailable, setAppleAvailable] = useState(false);

    // Methods
    const handleSuccessRedirect = () => {
        if (firstName) {
            toast.success(t("welcome_back", { firstName }));
        } else {
            toast.success(t("login_connected"));
        }
        // Sécurité : on n'autorise que les chemins internes pour éviter une
        // redirection ouverte (ex: ?redirect=https://site-malveillant.com).
        const isInternalRedirect =
            redirect &&
            redirect.startsWith("/") &&
            !redirect.startsWith("//") &&
            !redirect.startsWith("/\\");
        if (isInternalRedirect) {
            if (redirect == "/mentor") {
                router.push("/?firstTimeMentor=true");
                return;
            }
            router.push(redirect);
            return;
        }
        router.push("/");
    };

    const onSubmit = async (data) => {
        if (isLoading) return;
        setIsLoading(true);

        const response = await signIn("credentials", {
            email: data.email,
            password: data.password,
            redirect: false,
        });

        setIsLoading(false);

        if (response?.error) {
            // Le compte a la 2FA activée : on passe à l'étape de vérification
            if (response.error.includes("2FA_REQUIRED")) {
                setPendingAuth({
                    email: data.email,
                    password: data.password,
                });
                setCode("");
                setUseBackupCode(false);
                setStep(2);
                return;
            }
            if (response.error.includes("2FA_INVALID")) {
                toast.error(t("login_2fa_invalid"));
                return;
            }
            toast.error(response.error);
            return;
        }

        rememberLoginMethod("credentials");
        handleSuccessRedirect();
    };

    const onSubmitTotp = async (e) => {
        e.preventDefault();
        if (isLoading || !pendingAuth) return;
        if (!code.trim()) {
            toast.error(t("login_2fa_codeRequired"));
            return;
        }

        setIsLoading(true);

        const response = await signIn("credentials", {
            email: pendingAuth.email,
            password: pendingAuth.password,
            totp: code.trim(),
            recoveryCode: useBackupCode,
            rememberDevice: rememberDevice,
            redirect: false,
        });

        setIsLoading(false);

        if (response?.error) {
            if (
                response.error.includes("2FA_INVALID") ||
                response.error.includes("2FA_REQUIRED")
            ) {
                toast.error(t("login_2fa_invalid"));
                return;
            }
            toast.error(response.error);
            return;
        }

        rememberLoginMethod("credentials");
        handleSuccessRedirect();
    };

    const handleGoogleLogin = async () => {
        if (!isLoading) {
            setIsLoading(true);

            // On note l'intention avant la redirection vers Google (le code
            // après signIn ne s'exécute pas de façon fiable, la page redirige).
            rememberLoginMethod("google");

            // Connect user
            await signIn("google");
        }
    };

    const handleAppleLogin = async () => {
        if (!isLoading) {
            setIsLoading(true);

            // On note l'intention avant la redirection vers Apple (le code
            // après signIn ne s'exécute pas de façon fiable, la page redirige).
            rememberLoginMethod("apple");

            // Connect user
            await signIn("apple");
        }
    };

    useEffect(() => {
        // Lecture côté client uniquement (évite tout souci d'hydratation SSR)
        try {
            const stored = localStorage.getItem(LAST_LOGIN_KEY);
            if (
                stored === "google" ||
                stored === "apple" ||
                stored === "credentials"
            ) {
                setLastMethod(stored);
            }
        } catch (e) {
            // localStorage indisponible : pas de badge, ce n'est pas bloquant
        }
    }, []);

    useEffect(() => {
        // Affichage conditionnel du bouton Apple : uniquement si le provider
        // est réellement exposé par le serveur (activation progressive).
        let cancelled = false;
        getProviders()
            .then((providers) => {
                if (!cancelled) {
                    setAppleAvailable(!!providers?.apple);
                }
            })
            .catch(() => {
                // Impossible de récupérer les providers : pas de bouton Apple
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (queryError) {
            let message = "";
            switch (queryError) {
                case "EMAIL_ALREADY_USED":
                    message = t("login_emailAlreadyUsed");
                    break;
                case "GOOGLE_DISCONNECTED":
                    message = t("login_googleDisconnected");
                    break;
                default:
                    console.log(
                        "Erreur pendant inscription Google.com",
                        queryError,
                    );
                    message = t("login_unknowedError");
                    break;
            }

            toast.error(
                queryError == "Callback" ? t("login_unknowedError") : message,
            );
        }
    }, []);

    const lastUsedBadge = (
        <span className="absolute -top-2.5 right-2.5 z-10 inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-600 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
            {t("login_lastUsed")}
        </span>
    );

    return (
        <div className="flex min-h-svh flex-col items-center justify-center md:bg-muted p-6 md:p-10">
            <ToastContainer
                position="bottom-right"
                autoClose={8000}
                theme="dark"
                closeOnClick={true}
            />
            <div className="w-full max-w-sm md:max-w-3xl flex flex-col gap-6">
                <div className="hidden md:flex justify-center items-center">
                    <Link href="/">
                        <Logo ratio={1.5} icon />
                    </Link>
                </div>
                <Card className="overflow-hidden md:border border-none md:shadow shadow-none">
                    <div className="md:hidden flex justify-center items-center pt-6">
                        <Link href="/">
                            <Logo ratio={1.5} icon />
                        </Link>
                    </div>
                    <CardContent className="grid p-0 md:grid-cols-2">
                        <div className="p-6 md:p-8">

                {step === 1 ? (
                    <>
                        {/* Title */}
                        <h1 className="font-sourceSans text-3xl dark:text-white mt-4 font-bold text-left">
                            {firstName ? (
                                <>{t("welcome_back", { firstName })}</>
                            ) : (
                                t("login_title")
                            )}
                        </h1>
                        <p className="mt-1">{t("login_headline")}</p>
                        {/* Sign up with google  */}
                        <div className="relative mt-4">
                            {lastMethod === "google" && lastUsedBadge}
                            <Button
                                type="google"
                                block
                                onClick={handleGoogleLogin}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="0.98em"
                                        height="1em"
                                        viewBox="0 0 256 262"
                                    >
                                        <path
                                            fill="#4285F4"
                                            d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                                        ></path>
                                        <path
                                            fill="#34A853"
                                            d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                                        ></path>
                                        <path
                                            fill="#FBBC05"
                                            d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"
                                        ></path>
                                        <path
                                            fill="#EB4335"
                                            d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                                        ></path>
                                    </svg>
                                    {t("login_google")}
                                </div>
                            </Button>
                        </div>

                        {/* Sign in with Apple — affiché seulement si le
                            provider est configuré côté serveur */}
                        {appleAvailable && (
                            <div className="relative mt-4">
                                {lastMethod === "apple" && lastUsedBadge}
                                <Button
                                    type="google"
                                    block
                                    onClick={handleAppleLogin}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="0.98em"
                                            height="1em"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                fill="currentColor"
                                                d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                                            />
                                        </svg>
                                        {t("login_apple")}
                                    </div>
                                </Button>
                            </div>
                        )}

                        <div className="mt-4 flex items-center gap-3">
                            <div className="w-1/2 h-[1px] bg-gray-300"></div>
                            <div className="text-gray-400 dark:text-grayDark-400 text-sm">
                                {t("login_else")}
                            </div>
                            <div className="w-1/2 h-[1px] bg-gray-300"></div>
                        </div>

                        {/* Form */}
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="mt-5"
                        >
                            <p>
                                <label htmlFor="email" className="label">
                                    {t("login_email")}
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder={t("login_email")}
                                    className="input"
                                    {...register("email", {
                                        required: true,
                                        pattern:
                                            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    })}
                                />
                                {errors.email &&
                                    errors.email.type === "required" && (
                                        <small className="text-red-500">
                                            {t("login_email_required")}
                                        </small>
                                    )}
                                {errors.email &&
                                    errors.email.type === "pattern" && (
                                        <small className="text-red-500">
                                            {t("login_email_invalid")}
                                        </small>
                                    )}
                            </p>
                            <p>
                                <label htmlFor="password" className="label">
                                    {t("login_password")}
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    placeholder={t("login_password")}
                                    className="input"
                                    {...register("password", {
                                        required: true,
                                    })}
                                />
                                {errors.password && (
                                    <small className="text-red-500">
                                        {t("login_password_required")}
                                    </small>
                                )}
                            </p>
                            <div className="relative flex justify-end mt-5">
                                {lastMethod === "credentials" && lastUsedBadge}
                                <Button isLoading={isLoading} progress block>
                                    {t("login_submit")}
                                </Button>
                            </div>
                        </form>
                        <div className="mt-5">
                            <Link
                                href="/security/forgotten-password"
                                className="text-gray-500 text-sm"
                            >
                                {t("login_forgottenPassword")}
                            </Link>
                        </div>

                        {/* Other */}
                        <div className="mt-4 flex items-center gap-3">
                            <div className="w-1/2 h-[1px] bg-gray-300"></div>
                            <div className="text-gray-400 dark:text-grayDark-400 text-sm">
                                {t("login_or")}
                            </div>
                            <div className="w-1/2 h-[1px] bg-gray-300"></div>
                        </div>

                        <div className="mt-4 text-center">
                            <Link href="/signup" className="text-black text-sm">
                                {t("login_noAccount")}{" "}
                                <b>{t("login_createYourAccount")}</b>
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Étape 2 : double authentification */}
                        <h1 className="font-sourceSans text-3xl dark:text-white mt-4 font-bold text-left">
                            {t("login_2fa_title")}
                        </h1>
                        <p className="mt-1">
                            {useBackupCode
                                ? t("login_2fa_backupHint")
                                : t("login_2fa_hint")}
                        </p>

                        <form onSubmit={onSubmitTotp} className="mt-5">
                            <p>
                                <label htmlFor="totp" className="label">
                                    {useBackupCode
                                        ? t("login_2fa_backupLabel")
                                        : t("login_2fa_codeLabel")}
                                </label>
                                <input
                                    type="text"
                                    id="totp"
                                    autoFocus
                                    autoComplete="one-time-code"
                                    inputMode={
                                        useBackupCode ? "text" : "numeric"
                                    }
                                    placeholder={
                                        useBackupCode ? "XXXX-XXXX" : "123456"
                                    }
                                    className="input"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                />
                            </p>

                            <div className="flex items-center gap-2 mt-3">
                                <input
                                    type="checkbox"
                                    id="rememberDevice"
                                    checked={rememberDevice}
                                    onChange={(e) =>
                                        setRememberDevice(e.target.checked)
                                    }
                                />
                                <label
                                    htmlFor="rememberDevice"
                                    className="text-sm text-gray-600 dark:text-grayDark-300"
                                >
                                    {t("login_2fa_remember")}
                                </label>
                            </div>

                            <div className="flex justify-end mt-5">
                                <Button isLoading={isLoading} progress block>
                                    {t("login_2fa_submit")}
                                </Button>
                            </div>
                        </form>

                        <div className="mt-4 flex flex-col gap-2 items-start">
                            <button
                                type="button"
                                onClick={() => {
                                    setUseBackupCode(!useBackupCode);
                                    setCode("");
                                }}
                                className="text-gray-500 text-sm"
                            >
                                {useBackupCode
                                    ? t("login_2fa_useApp")
                                    : t("login_2fa_useBackup")}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setStep(1);
                                    setCode("");
                                    setPendingAuth(null);
                                }}
                                className="text-gray-500 text-sm"
                            >
                                {t("login_2fa_back")}
                            </button>
                        </div>
                    </>
                )}
                        </div>
                        <BelieversPanel />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
